import React, { useState, } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  TextField,
  Paper,
  CircularProgress,
  InputAdornment,
  IconButton, 
  Container,
} from "@mui/material";
import { useForm } from "react-hook-form";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { styled } from "@mui/material/styles";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { login, sentOtp, verifyOtp, forgotPassword } from "../services/api";
import "react-toastify/dist/ReactToastify.css";
import backgroundImage from "../assets/img/bgImg.jpg.webp";
import LoginIcon from "@mui/icons-material/Login";
import CloseIcon from "@mui/icons-material/Close";


// Styled components
const NavBar = styled(AppBar)({
  backgroundColor: "rgba(0, 0, 0, 0.8)",
});

const MainImage = styled(Box)({
  width: "100vw",
  height: "100vh",
  backgroundImage: `url(${backgroundImage})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  position: "absolute",
  top: 0,
  left: 0,
  zIndex: -1,
  backgroundRepeat: "no-repeat",
  objectFit: "cover",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
});

const PageContainer = styled(Box)({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  color: "white",
});

const ContentContainer = styled(Box)({
  flex: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  zIndex: 1,
  margin: "24px",
  backgroundColor: "transparent"
});

const LoginPaper = styled(Paper)({
  padding: "2rem",
  maxWidth: 400,
  width: "100%",
  backgroundColor: "transparent", // Semi-transparent background
  boxShadow: "25px 30px 120px rgba(0, 0, 0, 10)",
  borderRadius: "10px",
  border: "1px 2px 2px 0px solid balck",
  color: "white",
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "white" },
    "&:hover fieldset": { borderColor: "white" },
    "&.Mui-focused fieldset": { borderColor: "white" },
  },
  input: { color: "white" },
  "& label": { color: "white" },
  "& label.Mui-focused": { color: "white" },
});

 // Main component
const LoginPage = () => {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState("");
  const [displayLoginForm, setDisplayLoginForm] = useState(true);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const toggleNewPasswordVisibility = () => setShowNewPassword((prev) => !prev);


  const navigate = useNavigate();
  const { setUser } = useUser();
  const currentYear = new Date().getFullYear();
  const { register, handleSubmit } = useForm();
  const [email, setEmail] = useState("");

  

  const sendOtp = async (data) => {
    try {
      await sentOtp({ email: data.email });
      setEmail(data.email);
      setStep(2);
      toast.success("OTP sent to email");
    } catch (error) {
      toast.error(error.response?.data?.message || "Error sending OTP");
    }
  };

  const verifyOTP = async (data) => {
    try {
      await verifyOtp({ email, otp: data.otp });
      setStep(3);
      toast.success("OTP verified successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid OTP");
    }
  };

  const resetPassword = async (data) => {
    try {
      await forgotPassword({
        email,
        newPassword: data.password,
      });
      toast.success("Password reset successful");
      setStep("");
      setDisplayLoginForm(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error resetting password");
    }
  };

  // // Error handling logic for the login function
  const handleLoginError = (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.message;

    // Log error details for debugging
    console.error("Error Status:", status);
    console.error("Error Data:", message || error.response?.data);

    // Handle specific error cases
    if (status === 401) {
      toast.error("Invalid credentials. Please try again.");
    } else {
      toast.error("An unexpected error occurred. Please try again later.");
    }
  };
  const handleLogin = async (e) => {
    e.preventDefault(); // Prevent default form submission

    if (!userId || !password) {
      toast.error("Please fill in both fields");
      return;
    }

    setLoading(true); // Show loading indicator

    try {
      const response = await login({ userId, password });

      if (response.status === 200) {
        const { token, refreshToken, user, message } = response.data;
        console.log(response.data.message);

        if (message?.includes("Payment expired")) {
          toast.warn(message || "Payment expired. Please renew.");
          setTimeout(() => navigate("/QR"), 1000);
          return;
        }

        if (!token || !refreshToken) {
          toast.error("Login failed. Invalid token.");
          return;
        }

        // Store tokens in localStorage
        localStorage.setItem("authToken", token);
        localStorage.setItem("refreshToken", refreshToken);
        // Update user state
        setUser(user);
        toast.success("Login Successful!");

        setTimeout(() => navigate("/beverages/stocks"), 1000); // Redirect to home page
        return;
      }
    } catch (error) {
      handleLoginError(error);
    } finally {
      setLoading(false); // Hide loading indicator
    }
  };
  const handleFrogotPassword = () => {
    setDisplayLoginForm(false);
    setStep(1);

  }


  const onClose = () =>{
    setStep("");
    setDisplayLoginForm(true);
  }

  return (
    <PageContainer>
      <ToastContainer />
      <NavBar position="static" sx={{

        backgroundColor: "transparent"
      }}>
        <Toolbar>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: "bold",
              fontSize: "1.5rem",
              display: "flex",
              justifyContent: "center",

            }}
          >
            Beverage Inventory and Sales Management System
          </Typography>
        </Toolbar>
      </NavBar>

      {/* Background image */}
      <MainImage />

      {/* Login form */}
      <ContentContainer>
        {displayLoginForm && <LoginPaper >
          <Typography variant="h5" align="center" gutterBottom>
            Login
          </Typography>
          <form onSubmit={handleLogin}>
            <StyledTextField
              label="User ID"
              variant="outlined"
              fullWidth
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              margin="normal"
              required
            />
            <StyledTextField
              label="Password"
              variant="outlined"
              fullWidth
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? (
                        <Visibility sx={{ color: "white" }} />
                      ) : (
                        <VisibilityOff sx={{ color: "white" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              onClick={() => handleFrogotPassword()}
              sx={{ color: "white", display: "block", fontSize: "10px" }}
            >
              Forgot Password?
            </Button>
            <Button
              type="submit"
              color="white"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "rgba(59, 43, 43, 0.8)",
                borderRight: "1.5px solid white",
                borderBottom: "1.5px solid white",
                borderTop: "1px solid black",
                borderLeft: "1px solid black",
                color: "white",
                boxShadow: "4px 8px 10px rgba(0,0,0,1)",
                width: "70px",
                textTransform: "none",
                marginLeft: "260px",
                animation: "zoomEffect 2s infinite ease-in-out",
                "&:hover": {
                  color: "#00FF9C",
                  border: "0.5px solid #00FF9C",
                },
                "@keyframes zoomEffect": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.1)" }, // Slight zoom-in effect
                  "100%": { transform: "scale(1)" },
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#00FFCA" }} />
              ) : (
                <>
                  <LoginIcon sx={{
                    opacity: 1, fontSize: "20px", marginRight: "8px",
                  }} />
                  Login
                </>
              )}
            </Button>
          </form>
        </LoginPaper>}

        {step === 1 && (
          <form
            onSubmit={handleSubmit(sendOtp)}
            style={{
              background: "rgba(140, 119, 119, 0.9)",
              padding: "20px 40px",
              borderRadius: "12px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", // Soft shadow for depth
              maxWidth: "400px", // Keeps form compact
              width: "100%",
              margin: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              color:"white"
            }}
          >
            <IconButton onClick={onClose} sx={{marginLeft:"330px",color:"white" }}>
                <CloseIcon />
            </IconButton>
            <h5 style={{ marginBottom: "20px", color: "white" }}>User Verification</h5>
            <StyledTextField
              {...register("email")}
              label="Email"
              fullWidth
              margin="normal"
              required
              variant="outlined"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              style={{ marginTop: "15px" }}
            >
              Send OTP
            </Button>
          </form>
        )}


        {step === 2 && (
          <form
            onSubmit={handleSubmit(verifyOTP)}
            style={{
              background: "rgba(140, 119, 119, 0.9)",
              padding: " 20px 40px",
              borderRadius: "12px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              maxWidth: "400px",
              width: "100%",
              margin: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}
          >
            <IconButton onClick={onClose} sx={{marginLeft:"330px", color:"white" }}>
                <CloseIcon />
            </IconButton>
            <h5 style={{ marginBottom: "20px", color: "white" }}>OTP Verification</h5>
            <StyledTextField
              {...register("otp")}
              label="Enter OTP"
              fullWidth
              margin="normal"
              required
              variant="outlined"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              style={{ marginTop: "15px" }}
            >
              Verify OTP
            </Button>
          </form>
        )}

        {step === 3 && (
          <form
            onSubmit={handleSubmit(resetPassword)}
            style={{
              background: "rgba(140, 119, 119, 0.9)",
              padding: " 20px 40px",
              borderRadius: "12px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              maxWidth: "400px",
              width: "100%",
              margin: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center"
            }}
          >
            <IconButton onClick={onClose} sx={{marginLeft:"330px",color:"white" }}>
                <CloseIcon />
            </IconButton>
            <h5 style={{ marginBottom: "20px", color: "white" }}>Reset Password</h5>
            <StyledTextField
              {...register("password")}
              label="New Password"
              type="password"
              fullWidth
              margin="normal"
              required
              variant="outlined"
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              style={{ marginTop: "15px"}}
            >
              Reset Password
            </Button>
          </form>
        )}

      </ContentContainer>

      {/* Footer - Stays at bottom */}
      <Box
        component="footer"
        sx={{
          color: "white",
          padding: "1rem 1rem",
          bottom: 0,
          left: 0,
          right: 0,
          position: "sticky",
          borderRadius: "8px",  // Optional: rounded corners
          boxShadow: "0 0px 6px rgba(0, 0, 0,1 )",  // Optional: subtle shadow
        }}
      >

        <Container maxWidth="md">
          {/* Footer Bottom */}
          <Box textAlign="center">
            <Typography variant="body2" sx={{ color: "white", fontWeight: "bold",marginLeft:"80px" }}>
              <a style={{ fontSize: "15px" }}>©</a> {currentYear}  All Rights Reserved. <a target="_blank" href="https://techrovarit.com" style={{ color:"white", fontWeight: "bold" }}>TechRovar </a> IT Pvt Ltd.
            </Typography>

          </Box>
        </Container>
      </Box>
    </PageContainer>
  );
};

export default LoginPage;